import React, { useRef, useState } from "react";

import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/datasets/DatasetTable/FloatingWindow";
import { Callout } from "src/design-system/Callout";
import { JsonSchemaUnsavedChangesModal } from "src/jsonSchema/editor/JsonSchemaUnsavedChangesModal";
import {
  RawJsonSchemaEditor,
  type RawJsonSchemaEditorRef,
} from "src/jsonSchema/editor/RawJsonSchemaEditor";

export const RawJsonSchemaEditorWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data: windowData,
}) => {
  const { close: closeWindow } = useFloatingWindowsActions();
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const editorRef = useRef<RawJsonSchemaEditorRef>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [canSave, setCanSave] = useState(true);

  const handleSave = (rawSchema: string) => {
    windowData.onSave(rawSchema);
    setIsDirty(false);
    closeWindow(id);
  };

  const handleDiscard = () => {
    windowData.onDiscard?.();
    closeWindow(id);
  };

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmModal(true);
    } else {
      handleDiscard();
    }
  };

  const handleChange = (schema: string, dirty: boolean, isValid: boolean) => {
    setIsDirty(dirty);
    setCanSave(isValid);
    windowData.onChange?.(schema, dirty, isValid);
  };

  const handleConfirmDiscard = () => {
    setShowConfirmModal(false);
    handleDiscard();
  };

  return (
    <>
      <FloatingWindowInternal
        ref={windowRef}
        clipOverflow={false}
        draggable={false}
        height="h-195"
        isPinned={true}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title={
          <div className="mt-1 flex flex-col pr-3 pl-3">
            <div className="font-inter-semibold-12px text-gray-800">
              {windowData.title}
            </div>
            <div className="-mt-0.5 font-inter-normal-11px text-gray-500">
              {windowData.subtitle}
            </div>
          </div>
        }
        width="w-160"
        onClose={handleClose}
      >
        <RawJsonSchemaEditor
          ref={editorRef}
          initialSchema={windowData.initialSchema}
          topDecoration={
            windowData.infoText && (
              <Callout type="neutral">{windowData.infoText}</Callout>
            )
          }
          onChange={handleChange}
          onSave={handleSave}
        />
      </FloatingWindowInternal>

      <JsonSchemaUnsavedChangesModal
        canSave={canSave}
        open={showConfirmModal}
        onDiscard={handleConfirmDiscard}
        onKeepEditing={() => setShowConfirmModal(false)}
        onSave={() => editorRef.current?.save()}
      />
    </>
  );
};
